import { ExtraAccessValidatorI, ExtraAccessValidatorResultI } from '@interfaces/extra-access-validator.interface';
import { firstValueFrom } from 'rxjs';
import { GlobalStoreService } from '@modules/global/store/global-store.service';
import { Injectable } from '@angular/core';
import { LLMModelE } from '@enums/llm-models.enum';
import { PermissionType, ResourceNames } from '@enums/acl/resources.enum';
import { UserHelperService } from '@helpers/user-helper.service';

@Injectable({ providedIn: 'root' })
export class ExtraValidationsService {
  constructor(private globalStoreService: GlobalStoreService, private readonly userHelperService: UserHelperService) {}

  private validators: ExtraAccessValidatorI[] = [
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.AI_AGENT,
      validate: async () => {
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return !!customerSettings?.llmModelName && customerSettings?.llmModelName !== LLMModelE.NONE;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.AI_AGENT__MENU,
      validate: async () => {
        if (this.userHelperService.isSuperAdmin()) return true;
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return customerSettings?.hasLegacyAiAgents ?? false;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.WORKFLOW,
      validate: async () => {
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return typeof customerSettings?.useWorkflow === 'boolean' ? customerSettings?.useWorkflow : false;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.SUPERVISION__QUALITY_MONITORING,
      bypassAcl: true,
      validate: async () => {
        const ALLOWED_CUSTOMER_ID = '69b317ec3e6a94568ceb7a6b';
        const atts = this.userHelperService.getUserAttributes() as any;
        return true;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.MONITORING__AGENTS,
      validate: async () => {
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return typeof customerSettings?.useMonitoring === 'boolean' ? customerSettings?.useMonitoring : false;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.MONITORING__TEAMS,
      validate: async () => {
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return typeof customerSettings?.useMonitoring === 'boolean' ? customerSettings?.useMonitoring : false;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.MONITORING__CAMPAIGNS,
      validate: async () => {
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return typeof customerSettings?.useMonitoring === 'boolean' ? customerSettings?.useMonitoring : false;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.MONITORING__DIALER,
      validate: async () => {
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return (
          (typeof customerSettings?.useMonitoring === 'boolean' ? customerSettings?.useMonitoring : false) &&
          (typeof customerSettings?.useDialer === 'boolean' ? customerSettings?.useDialer : false)
        );
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.REPORTS_TELEPHONY__RECORD,
      validate: async () => {
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return typeof customerSettings?.useModulePabx === 'boolean' ? customerSettings?.useModulePabx : false;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.REPORTS_TELEPHONY__DIALER,
      validate: async () => {
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return typeof customerSettings?.useDialer === 'boolean' ? customerSettings?.useDialer : false;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.REPORTS_TELEPHONY__CALLS,
      validate: async () => {
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return typeof customerSettings?.useModulePabx === 'boolean' ? customerSettings?.useModulePabx : false;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.SUPERVISION__DIALER_CAMPAIGN,
      validate: async () => {
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return typeof customerSettings?.useDialer === 'boolean' ? customerSettings?.useDialer : false;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.REPORTS__TEMPLATE_LIMIT,
      validate: async () => {
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return typeof customerSettings?.useTemplateLimitReport === 'boolean'
          ? customerSettings?.useTemplateLimitReport
          : false;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.REPORTS__AWAY,
      validate: async () => {
        if (this.userHelperService.isSuperAdmin()) {
          return true;
        }
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return typeof customerSettings?.useAwayReport === 'boolean' ? customerSettings?.useAwayReport : false;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.REPORTS__BLOCK,
      validate: async () => {
        if (this.userHelperService.isSuperAdmin()) {
          return true;
        }
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return typeof customerSettings?.useBlockReport === 'boolean' ? customerSettings?.useBlockReport : false;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.CAMPAIGNS__CONTACT_LIST,
      validate: async () => {
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return typeof customerSettings?.useMailMarketing === 'boolean' ? customerSettings?.useMailMarketing : false;
      },
    },
    {
      permission: PermissionType.LIST_ALL,
      resource: ResourceNames.CAMPAIGNS__EMAIL,
      validate: async () => {
        const customerSettings = await firstValueFrom(this.globalStoreService.customerSettings$);
        return typeof customerSettings?.useMailMarketing === 'boolean' ? customerSettings?.useMailMarketing : false;
      },
    },
  ];

  public async getAllValidators(): Promise<ExtraAccessValidatorResultI[]> {
    return Promise.all(
      this.validators.map(async (v) => ({
        permission: v.permission,
        resource: v.resource,
        bypassAcl: v.bypassAcl,
        isValid: await v.validate(),
      }))
    );
  }

  public async validateListAll(resource: ResourceNames): Promise<boolean> {
    const validator = this.validators.find((v) => v.permission === PermissionType.LIST_ALL && v.resource === resource);
    if (validator) {
      return validator.validate();
    }
    return true;
  }
}
